import axios, { AxiosError, AxiosResponse } from 'axios';
import { isValidProposalId } from "../components/Chat/proposalTypes";
import { auth } from '../firebase';
import { ChatWelcomeResponse } from "../components/Chat/welcomeTypes";
import type { ConversationSummary, ServerChatMessage } from "../components/Chat/storage/serverMessages";
import type { ChatCapabilities } from "../components/Chat/panelHint";

interface ApiError {
  message?: string;
}

export const backendAPI = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  withCredentials: true,
});

interface Business {
  id: number;
  name: string;
  registryId: string;
  status: string;
  location: string;
  businessId: number;
  userId: number;
  createdAt: string;
  updatedAt: string;
  templateId?: number | null;
  logo?: string | null;
  details?: {
    description?: string;
    investment_level?: string;
    sector?: string;
    scalability?: string;
    economic_activity?: string;
    business_model?: string;
  };
}

export const chat_query = async (
  business: Business,
  text: string,
  history: { role: 'user' | 'assistant' | 'system'; content: string }[],
  onToken: (token: string) => void,
  options?: {
    conversationId?: string;
    sessionContext?: {
      phase?: string;
      agentMode?: string;
      mode?: string;
      onboardingComplete?: boolean;
      uiContext?: {
        panel: string | null;
        module: string | null;
        section: string | null;
        filter: string | null;
        entityKind: string | null;
        selectedEntityId: string | null;
        selectedEntityName: string | null;
        previousCapabilityId: string | null;
      } | null;
    };
    useServerHistory?: boolean;
  }
  ): Promise<{
    conversationId?: string;
    promptVersion?: string;
    queryIntent?: string;
  }> => {
    const user = auth.currentUser;
    if (!user) throw new Error("User not logged in");
    const idToken = await user.getIdToken();

    const body: Record<string, unknown> = {
      business,
      text,
      sessionContext: options?.sessionContext,
    };

    if (options?.conversationId) {
      body.conversationId = options.conversationId;
    }

    if (!options?.useServerHistory) {
      body.history = history;
    }

    const response = await fetch(`${process.env.REACT_APP_API_URL}chat/query`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${idToken}`,
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify(body),
    });

    if (!response.ok || !response.body) {
      throw new Error('Failed to connect to chat endpoint');
    }

    const conversationId =
      response.headers.get('X-Conversation-Id') || options?.conversationId || undefined;
    const promptVersion = response.headers.get('X-Prompt-Version') || undefined;
    const queryIntent = response.headers.get('X-Query-Intent') || undefined;

    const reader = response.body.getReader();
    const decoder = new TextDecoder('utf-8');

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      const chunk = decoder.decode(value);
      onToken(chunk);
    }

    return { conversationId, promptVersion, queryIntent };
};

export const chat_query_business = async (text: string) => {
  try {
    const user = auth.currentUser;
    if (!user) throw new Error("User not logged in");
    const idToken = await user.getIdToken();

    const response = await backendAPI.post(
      'chat/',
      { text },
      {
        headers: {
          Authorization: `Bearer ${idToken}`,
        }
      }   
    );
    return response;
  } catch (error: any) {
    throw new Error((error as AxiosError<ApiError>)?.response?.data?.message || 'Chat query failed');
  }
};

export const chat_welcome = async (
  businessId: number,
  options?: {
    openingOverride?: string;
    isReturningUser?: boolean;
    networkOptIn?: boolean;
    fromOnboarding?: boolean;
  }
): Promise<AxiosResponse<ChatWelcomeResponse>> => {
  try {
    const user = auth.currentUser;
    if (!user) throw new Error("User not logged in");
    const idToken = await user.getIdToken();

    const response = await backendAPI.get<ChatWelcomeResponse>(
      'chat/welcome',
      {
        params: {
          businessId,
          openingOverride: options?.fromOnboarding ? undefined : options?.openingOverride,
          isReturningUser: options?.isReturningUser ? 'true' : undefined,
          networkOptIn: options?.networkOptIn ? 'true' : undefined,
          fromOnboarding: options?.fromOnboarding ? 'true' : undefined,
        },
        headers: {
          Authorization: `Bearer ${idToken}`,
        },
      }
    );
    return response;
  } catch (error: unknown) {
    throw new Error((error as AxiosError<ApiError>)?.response?.data?.message || 'Chat welcome failed');
  }
};

export const query_incorporation = async (
  business: Business,
  ): Promise<
    { title: string; description: string }[] // Adjust based on the actual format
  > => {
  const user = auth.currentUser;
  if (!user) throw new Error("User not logged in");
  const idToken = await user.getIdToken();
  
  const response = await fetch(`${process.env.REACT_APP_API_URL}chat/query_incorporation`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${idToken}`,
      'Content-Type': 'application/json',
    },
    credentials: 'include',
    body: JSON.stringify({
      business,
    }),
  });

  if (!response.ok) {
    throw new Error('Failed to connect to chat endpoint');
  }

  // Expect full JSON response like: { steps: [...] }
  const result = await response.json();
  return result.steps;
};

export const get_chat_conversations = async (
  businessId: number,
  options?: { status?: string; search?: string; limit?: number }
): Promise<{ conversations: ConversationSummary[] }> => {
  const user = auth.currentUser;
  if (!user) throw new Error("User not logged in");
  const idToken = await user.getIdToken();

  const response = await backendAPI.get('chat/conversations', {
    params: {
      businessId,
      status: options?.status,
      search: options?.search,
      limit: options?.limit,
    },
    headers: {
      Authorization: `Bearer ${idToken}`,
    },
  });

  return response.data;
};

export const create_chat_conversation = async (payload: {
  businessId: number;
  title?: string;
  topic?: string;
}): Promise<ConversationSummary> => {
  const user = auth.currentUser;
  if (!user) throw new Error("User not logged in");
  const idToken = await user.getIdToken();

  const response = await backendAPI.post('chat/conversations', payload, {
    headers: {
      Authorization: `Bearer ${idToken}`,
    },
  });

  return response.data;
};

export const seed_conversation_welcome = async (
  conversationId: string,
  businessId: number
): Promise<{ seeded: boolean }> => {
  const user = auth.currentUser;
  if (!user) throw new Error("User not logged in");
  const idToken = await user.getIdToken();

  const response = await backendAPI.post(
    `chat/conversations/${conversationId}/seed-welcome`,
    { businessId },
    {
      headers: {
        Authorization: `Bearer ${idToken}`,
      },
    }
  );

  return response.data;
};

export const get_chat_capabilities = async (): Promise<ChatCapabilities> => {
  const user = auth.currentUser;
  if (!user) throw new Error("User not logged in");
  const idToken = await user.getIdToken();

  const response = await backendAPI.get("chat/capabilities", {
    headers: {
      Authorization: `Bearer ${idToken}`,
    },
  });

  return response.data;
};

export const update_chat_conversation = async (
  conversationId: string,
  payload: { title?: string; topic?: string; status?: "active" | "archived" }
): Promise<ConversationSummary> => {
  const user = auth.currentUser;
  if (!user) throw new Error("User not logged in");
  const idToken = await user.getIdToken();

  const response = await backendAPI.patch(`chat/conversations/${conversationId}`, payload, {
    headers: {
      Authorization: `Bearer ${idToken}`,
    },
  });

  return response.data;
};

export const delete_chat_conversation = async (
  conversationId: string
): Promise<{ deleted: boolean; id: string }> => {
  const user = auth.currentUser;
  if (!user) throw new Error("User not logged in");
  const idToken = await user.getIdToken();

  const response = await backendAPI.delete(`chat/conversations/${conversationId}`, {
    headers: {
      Authorization: `Bearer ${idToken}`,
    },
  });

  return response.data;
};

export const get_conversation_messages = async (
  conversationId: string,
  options?: { before?: string; limit?: number }
): Promise<{ messages: ServerChatMessage[]; hasMore: boolean }> => {
  const user = auth.currentUser;
  if (!user) throw new Error("User not logged in");
  const idToken = await user.getIdToken();

  const response = await backendAPI.get(`chat/conversations/${conversationId}/messages`, {
    params: {
      before: options?.before,
      limit: options?.limit,
    },
    headers: {
      Authorization: `Bearer ${idToken}`,
    },
  });

  return response.data;
};

export const import_local_messages = async (
  businessId: number,
  messages: Record<string, unknown>[]
): Promise<{ conversationId: string; imported: number }> => {
  const user = auth.currentUser;
  if (!user) throw new Error("User not logged in");
  const idToken = await user.getIdToken();

  const response = await backendAPI.post(
    'chat/conversations/import-local',
    { businessId, messages },
    {
      headers: {
        Authorization: `Bearer ${idToken}`,
      },
    }
  );

  return response.data;
};

export const get_proposals = async (
  businessId: number,
  options?: { status?: string; limit?: number }
): Promise<{ enabled: boolean; proposals: import("../components/Chat/proposalTypes").BusinessProposal[] }> => {
  const user = auth.currentUser;
  if (!user) throw new Error("User not logged in");
  const idToken = await user.getIdToken();

  const response = await backendAPI.get("proposals", {
    params: {
      businessId,
      status: options?.status,
      limit: options?.limit,
    },
    headers: {
      Authorization: `Bearer ${idToken}`,
    },
  });

  return response.data;
};

export const get_proposal = async (
  proposalId: string
): Promise<import("../components/Chat/proposalTypes").BusinessProposal> => {
  if (!isValidProposalId(proposalId)) {
    throw new Error("Invalid proposal id");
  }
  const user = auth.currentUser;
  if (!user) throw new Error("User not logged in");
  const idToken = await user.getIdToken();

  const response = await backendAPI.get(`proposals/${proposalId}`, {
    headers: {
      Authorization: `Bearer ${idToken}`,
    },
  });

  return response.data;
};

export const approve_proposal = async (
  proposalId: string
): Promise<{ proposal: import("../components/Chat/proposalTypes").BusinessProposal; summary?: string }> => {
  if (!isValidProposalId(proposalId)) {
    throw new Error("Invalid proposal id");
  }
  const user = auth.currentUser;
  if (!user) throw new Error("User not logged in");
  const idToken = await user.getIdToken();

  const response = await backendAPI.post(
    `proposals/${proposalId}/approve`,
    {},
    {
      headers: {
        Authorization: `Bearer ${idToken}`,
      },
    }
  );

  return response.data;
};

export const reject_proposal = async (
  proposalId: string,
  feedback?: string
): Promise<{ proposal: import("../components/Chat/proposalTypes").BusinessProposal }> => {
  if (!isValidProposalId(proposalId)) {
    throw new Error("Invalid proposal id");
  }
  const user = auth.currentUser;
  if (!user) throw new Error("User not logged in");
  const idToken = await user.getIdToken();

  const response = await backendAPI.post(
    `proposals/${proposalId}/reject`,
    { feedback },
    {
      headers: {
        Authorization: `Bearer ${idToken}`,
      },
    }
  );

  return response.data;
};
